"use client"

import { Box } from "@mui/material"
import { useSearchParams } from "next/navigation"
import { Suspense, useEffect, useLayoutEffect } from "react"
import { Container, CustomBreadcrumbs } from "ui"
import { extractProperty, getBreadCrumbs } from "utils"
import { storeReferralSource } from "utils/referral"
import Jobs from "./Jobs/Jobs"
import CareerEmployeeTestimonials from "./components/CareerEmployeeTestimonials"
import CareerHero from "./components/CareerHero"
import CareerJoinUs from "./components/CareerJoinUs"
import CareerLobbyBanner from "./components/CareerLobbyBanner"

export type CareerLobbyMessages = {
  findJob: string
  company: string
  all: string
  location: string
  jobType: string
  jobCategory: string
  requiredFields: string
  jobsBoard: string
  viewResults: string
  jobNo: string
  jobDescr: string
  viewJob: string
  more: string
  search: string
  filters: string
  clearAll: string
  searchButtonLabel: string
  moreJobs: string
}

type Props = {
  data: any
  filters?: any
  jobs?: any
  locale: string
  messages: CareerLobbyMessages
  allData: any
  companyDivisionId: number[]
  isGroupWebsite?: boolean
}

function Careers({
  data,
  filters,
  jobs,
  locale,
  messages,
  allData,
  companyDivisionId = [],
  isGroupWebsite,
}: Props) {
  const searchParams = useSearchParams()
  const dataToRender = data?.page
  const { selfObject, parents } = getBreadCrumbs(data, locale)

  const heroBannerData = extractProperty(dataToRender, "heroBanner")?.[0]
  const jobSearchData = extractProperty(dataToRender, "jobSearch")?.[0]
  const noJobsFoundData = extractProperty(dataToRender, "searchBarNoResults")?.[0]
  const careerLobbyBanner = extractProperty(dataToRender, "careerLobbyBanner")?.[0]
  const joinUs = extractProperty(dataToRender, "JoinUs")?.[0]
  const employeeTestimonials = extractProperty(dataToRender, "EmployeeTestimonials")?.[0]

  useLayoutEffect(() => {
    const mainContent = document.getElementById("mainContent")
    if (!mainContent) return
    const previousPaddingBottom = mainContent.style.paddingBottom
    mainContent.style.paddingBottom = "0"

    return () => {
      mainContent.style.paddingBottom = previousPaddingBottom
    }
  }, [])

  useEffect(() => {
    storeReferralSource(searchParams)
  }, [searchParams])

  return (
    <Box
      sx={{
        fontFamily: "var(--narkis) !important",
        "& * ": { fontFamily: "inherit !important" },
      }}
    >
      <Container>
        <CustomBreadcrumbs
          desktopFontSize={18}
          mobileFontSize={16}
          current={selfObject}
          parents={parents}
        />
      </Container>
      {heroBannerData && <CareerHero data={heroBannerData} />}
      {filters && jobs && locale === "he" && (
        <Jobs
          filters={filters}
          jobs={jobs}
          locale={locale}
          messages={messages}
          allData={allData}
          jobSearchData={jobSearchData}
          noJobsFoundData={noJobsFoundData}
          companyDivisionId={companyDivisionId}
          isGroupWebsite={isGroupWebsite}
        />
      )}
      {careerLobbyBanner && <CareerLobbyBanner data={careerLobbyBanner} />}
      {joinUs && <CareerJoinUs data={joinUs} />}
      {employeeTestimonials && <CareerEmployeeTestimonials data={employeeTestimonials} />}
    </Box>
  )
}

function CareersWithSuspense(props: Props) {
  return (
    <Suspense>
      <Careers {...props} />
    </Suspense>
  )
}

export default CareersWithSuspense
